/**
 * Types INFERRED from the decision registry. Because {@link DECISIONS} is
 * declared `as const satisfies Record<string, AnyDecision>`, every option-id
 * literal survives — adding a decision or option to the registry extends
 * {@link GameSettings} (and everything downstream) with zero manual union
 * declarations. See the "type inference lock" describe in
 * test/spec.compile.test.ts for the compile-time lock.
 */
import type { KeyBindings } from '@djgg/controls';
import type { AnyDecision } from './registry.js';
import { DECISIONS } from './registry.js';

type Registry = typeof DECISIONS;

/** A decision id (a key of the registry). Stable, never renamed. */
export type DecisionId = keyof Registry;

/** The stored value type of one decision. `multi` decisions store a SET of
 *  option ids (a readonly array; order is irrelevant). */
type ValueOf<D extends AnyDecision> = D extends {
  kind: 'choice';
  options: readonly { id: infer I extends string }[];
}
  ? I
  : D extends { kind: 'multi'; options: readonly { id: infer I extends string }[] }
    ? readonly I[]
    : D extends { kind: 'palette'; presets: readonly { id: infer P extends string }[] }
      ? P
      : never;

/**
 * The COMPLETE settings object — what the prompt compiler consumes and what
 * a version doc snapshots. Produced only by
 * {@link import('./validate.js').completeSettings}; never stored sparse.
 */
export type GameSettings = { readonly [K in DecisionId]: ValueOf<Registry[K]> };

/**
 * The creator's ANSWERED subset — what `creations/{id}.settings` stores.
 * Sparse by design: derived defaults are recomputed at completion time and
 * never masquerade as creator choices. `keyBindings` is the one NON-decision
 * extra (a structured remap the chip registry can't express): validated by
 * `@djgg/controls` inside checkSettings, applied LIVE via the /play proxy,
 * and stripped by completion (a dense {@link GameSettings} stays decisions
 * only).
 */
export type PartialGameSettings = Partial<GameSettings> & {
  readonly keyBindings?: Partial<KeyBindings>;
};

/** All decision ids, in registry (presentation) order. */
export const DECISION_IDS = Object.keys(DECISIONS) as readonly DecisionId[];

/** Look up a decision definition by id. */
export function decisionOf(id: DecisionId): AnyDecision {
  return DECISIONS[id];
}
